import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Save, X, Package } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProductConfigPage() {
  const { token } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [allTools, setAllTools] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [editingId, setEditingId] = useState(null); // null = not editing, 'new' = creating
  const [formName, setFormName] = useState('');
  const [formTools, setFormTools] = useState([]);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchConfigs = useCallback(async () => {
    try {
      const [configsRes, toolsRes] = await Promise.all([
        fetch(`${API}/api/product-configs`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/product-configs/tools`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (configsRes.ok) setConfigs(await configsRes.json());
      if (toolsRes.ok) setAllTools(await toolsRes.json());
    } catch {
      toast.error('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const startCreate = () => {
    setEditingId('new');
    setFormName('');
    setFormTools([]);
  };

  const startEdit = (config) => {
    setEditingId(config.id);
    setFormName(config.name);
    setFormTools([...config.tools]);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormName('');
    setFormTools([]);
  };

  const toggleTool = (key) => {
    setFormTools(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const selectAll = () => setFormTools(allTools.map(t => t.key));
  const deselectAll = () => setFormTools([]);

  const handleSave = async () => {
    if (!formName.trim()) { toast.error('Configuration name is required'); return; }
    if (formTools.length === 0) { toast.error('Select at least one tool'); return; }
    try {
      const isNew = editingId === 'new';
      const url = isNew ? `${API}/api/product-configs` : `${API}/api/product-configs/${editingId}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, { method, headers, body: JSON.stringify({ name: formName.trim(), tools: formTools }) });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail || 'Save failed'); return; }
      toast.success(isNew ? 'Configuration created' : 'Configuration updated');
      cancelEdit();
      fetchConfigs();
    } catch { toast.error('Save failed'); }
  };

  const handleDelete = async (config) => {
    if (!window.confirm(`Delete "${config.name}"? Any companies using this configuration will be unassigned.`)) return;
    try {
      const res = await fetch(`${API}/api/product-configs/${config.id}`, { method: 'DELETE', headers });
      if (!res.ok) { toast.error('Delete failed'); return; }
      toast.success('Configuration deleted');
      if (editingId === config.id) cancelEdit();
      fetchConfigs();
    } catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="product-config-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/assets/product-config-icon.png" alt="" className="h-8 w-8 object-contain" />
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Product Configuration</h1>
            <p className="text-sm text-slate-500">Define which tools are available for each product tier</p>
          </div>
        </div>
        {editingId === null && (
          <Button onClick={startCreate} data-testid="create-config-btn" className="gap-2">
            <Plus className="h-4 w-4" /> New Configuration
          </Button>
        )}
      </div>

      {/* Create / Edit Form */}
      {editingId !== null && (
        <Card className="border-violet-200 bg-violet-50/30" data-testid="config-form">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{editingId === 'new' ? 'New Configuration' : 'Edit Configuration'}</CardTitle>
            <CardDescription>Choose a name and select the tools to include</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Configuration Name</label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. Standard, Premium, Enterprise"
                data-testid="config-name-input"
                className="max-w-sm"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-700">Tools</label>
                <div className="flex gap-2">
                  <button onClick={selectAll} className="text-xs text-violet-600 hover:underline">Select All</button>
                  <span className="text-xs text-slate-300">|</span>
                  <button onClick={deselectAll} className="text-xs text-violet-600 hover:underline">Deselect All</button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="tools-checklist">
                {allTools.map(tool => (
                  <label
                    key={tool.key}
                    className={`flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors ${
                      formTools.includes(tool.key)
                        ? 'border-violet-300 bg-violet-50 text-violet-900'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <Checkbox
                      checked={formTools.includes(tool.key)}
                      onCheckedChange={() => toggleTool(tool.key)}
                      data-testid={`tool-checkbox-${tool.key}`}
                    />
                    <span className="text-sm font-medium">{tool.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-2">{formTools.length} of {allTools.length} tools selected</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleSave} data-testid="save-config-btn" className="gap-2">
                <Save className="h-4 w-4" /> Save
              </Button>
              <Button variant="outline" onClick={cancelEdit} className="gap-2">
                <X className="h-4 w-4" /> Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Configurations List */}
      {configs.length === 0 && editingId === null ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Package className="h-10 w-10 text-slate-300 mb-3" />
            <p className="text-sm text-slate-500 mb-1">No configurations yet</p>
            <p className="text-xs text-slate-400">Create your first product configuration to define tool access per company</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4" data-testid="configs-list">
          {configs.map(config => (
            <Card
              key={config.id}
              className={editingId === config.id ? 'ring-2 ring-violet-300' : ''}
              data-testid={`config-card-${config.id}`}
            >
              <CardContent className="py-4 flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900">{config.name}</h3>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {config.tools.map(toolKey => {
                      const tool = allTools.find(t => t.key === toolKey);
                      return (
                        <span key={toolKey} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                          {tool ? tool.label : toolKey}
                        </span>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    {config.tools.length} tool{config.tools.length !== 1 ? 's' : ''} configured
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => startEdit(config)}
                    disabled={editingId !== null}
                    data-testid={`edit-config-${config.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(config)}
                    disabled={editingId !== null}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-config-${config.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
